import { usePricingRights } from '@util/PricingRightsHook';
import { pricingDayAction } from '@util/PricingRights';
import React from 'react';
import { PaperAirplaneIcon, EyeIcon } from '@heroicons/react/16/solid';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import FormButton from '@app/component/form/button/FormButton';
import PriceRecommendationStateSnapshot from '@app/model/market_state/output/PriceRecommendationStateSnapshot';
import { DrawerProps } from '@app/component/overlays/drawer/Drawer';
import useDrawer from '@app/component/overlays/drawer/DrawerRef';
import MarketStateSnapshotCalendarDayPriceRecommendationDrawer
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/overlay/MarketStateSnapshotCalendarDayPriceRecommendationDrawer';

const MarketStateSnapshotCalendarDayTableRowPriceSubmissionAction: React.FunctionComponent<{
	priceRecommendationState: PriceRecommendationStateSnapshot | undefined,
	statDate: string
}> = ({ priceRecommendationState, statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { canViewRecommendations, canChangePrices } = usePricingRights(companyHotel);
	const action = pricingDayAction(canViewRecommendations, canChangePrices, priceRecommendationState !== undefined);
	if (action === null || action === undefined) return null;

	// One Day Pricing drawer on every day: with a valid recommendation, a held one, or no engine result.
	return <RenderDayPricingButton readOnly={action === 'view'} statDate={statDate} />;
};

type RenderDayPricingButtonProps = Omit<DrawerProps, 'title' | 'ref'> & {
	readOnly: boolean,
	statDate: string,
}

// FormButton forwards no title, so the hover text sits on a wrapper span; the sr-only span names the button.
const RenderButton: React.FunctionComponent<{ onClick: () => any, readOnly?: boolean }> = ({ onClick, readOnly = false }) => <span
	title={readOnly ? 'View recommendation' : 'Change price'}>
	<FormButton
		type="button"
		state="text"
		onClick={onClick}
		className="inline-flex h-7 w-7 items-center justify-center rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
	>
		<span className="sr-only">{readOnly ? 'View recommendation' : 'Change price'}</span>
		{readOnly ? <EyeIcon className="h-4 w-4" aria-hidden="true" /> : <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />}
	</FormButton>
</span>;

const RenderDayPricingButton = React.memo(
	function RenderDayPricingButton({ statDate, readOnly, ...props }: RenderDayPricingButtonProps) {
		const drawer = useDrawer();

		return <>
			<RenderButton readOnly={readOnly} onClick={() => drawer.open()} />
			<MarketStateSnapshotCalendarDayPriceRecommendationDrawer statDate={statDate} {...props} ref={drawer.ref} />
		</>;
	});

export default MarketStateSnapshotCalendarDayTableRowPriceSubmissionAction;
